// Coverage & Roadmap surface — Slice 1 pure mirror. Types for the
// get_coverage_overview() payload + pure bucketing for the UI. No
// I/O (the hook arrives in Slice 2). HONEST: this only reshapes what
// the RPC returns — `live` is the gate's own truth, `activating` is
// registered-but-dark football (never pickable), `roadmap` is the
// registry. Nothing here can promote a dormant sport.

export interface LiveLeague {
  readonly sport_code: string;
  readonly league_code: string;
  readonly league_name: string;
  readonly odds_supported: boolean;
}

export interface ActivatingLeague {
  readonly league_code: string;
  readonly league_name: string;
  readonly phase: string;
  readonly odds_supported: boolean;
  /** Verified web-search corpus + the activation minimum. Optional
   *  for back-compat; readiness is null when absent (honest). */
  readonly web_corpus?: number;
  readonly corpus_min?: number;
}

export interface RoadmapSport {
  readonly sport_code: string;
  readonly display_name: string;
  readonly phase: string;
  readonly status: "live" | "calibrating" | "planned";
  readonly team_sport: boolean;
}

export interface CoverageAccess {
  readonly tier: string;
  readonly expansion_quota: number; // -1 = all, 0 = football only
  readonly football_always: boolean;
}

export interface CoverageOverview {
  readonly live: readonly LiveLeague[];
  readonly football_activating: readonly ActivatingLeague[];
  readonly roadmap: readonly RoadmapSport[];
  readonly access: CoverageAccess;
  readonly generated_at: string;
}

export interface PhaseGroup<T> {
  readonly phase: string;
  readonly items: readonly T[];
}

function groupByPhase<T extends { phase: string }>(
  rows: readonly T[],
): PhaseGroup<T>[] {
  const m = new Map<string, T[]>();
  for (const r of rows) {
    const k = r.phase;
    const list = m.get(k);
    if (list) list.push(r);
    else m.set(k, [r]);
  }
  return [...m.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([phase, items]) => ({ phase, items }));
}

export interface CoverageBuckets {
  /** Genuinely live today (the gate's truth). */
  readonly liveNow: readonly LiveLeague[];
  /** Football leagues registered but still gate-dark, grouped by
   *  phase. UI MUST label these "coming / gated", never pickable. */
  readonly footballActivating: readonly PhaseGroup<ActivatingLeague>[];
  /** Other sports by phase (registry roadmap, excludes football
   *  which is represented by liveNow + footballActivating). */
  readonly sportRoadmap: readonly PhaseGroup<RoadmapSport>[];
  readonly liveCount: number;
  readonly comingCount: number;
}

/** Pure: reshape the RPC payload into display buckets. Honest —
 *  liveNow is copied verbatim from `live` (gate truth); a dormant
 *  league can never appear there. */
export function bucketCoverage(o: CoverageOverview): CoverageBuckets {
  const sportRoadmap = groupByPhase(
    o.roadmap.filter((s) => s.sport_code !== "football"),
  );
  const footballActivating = groupByPhase(o.football_activating);
  return {
    liveNow: o.live,
    footballActivating,
    sportRoadmap,
    liveCount: o.live.length,
    comingCount:
      o.football_activating.length +
      o.roadmap.filter((s) => s.status !== "live").length,
  };
}

export type AccessKind = "football_only" | "football_plus_n" | "all_sports";

/** Pure: a plain-English access summary from the tier quota.
 *  -1 ⇒ all live sports; 0 ⇒ football only; n>0 ⇒ football + n. */
export function describeAccess(a: CoverageAccess): {
  kind: AccessKind;
  label: string;
} {
  if (a.expansion_quota === -1) {
    return { kind: "all_sports", label: "All live sports" };
  }
  if (a.expansion_quota > 0) {
    return {
      kind: "football_plus_n",
      label: `Football + ${a.expansion_quota} expansion sport${
        a.expansion_quota === 1 ? "" : "s"
      } (your choice once live)`,
    };
  }
  return { kind: "football_only", label: "Football only" };
}

// ── Slice 4 — tier-aware access matrix ───────────────────────────
// Honest framing: this says, for each ROADMAP sport, what the
// caller's CURRENT plan would grant WHEN that sport goes live. It
// never implies the sport is available now (all are dormant) and
// never fabricates access — it is pure quota arithmetic.

export type SportAccessState =
  | "included" // tier covers all live sports (quota -1)
  | "choose" // tier grants N expansion picks; this is eligible
  | "upgrade"; // tier (football-only) would need an upgrade

export interface SportAccessRow {
  readonly sport_code: string;
  readonly display_name: string;
  readonly phase: string;
  readonly state: SportAccessState;
}

export interface SportAccessMatrix {
  readonly rows: readonly SportAccessRow[];
  /** Plain-English hint, e.g. "Upgrade to Pro to choose 1 sport". */
  readonly upgradeHint: string | null;
}

/** Pure: per-roadmap-sport access given the caller's expansion
 *  quota. Football is excluded (always included, never a "pick").
 *  -1 ⇒ everything included; n>0 ⇒ each is a "choose" slot; 0 ⇒
 *  upgrade. No fabrication — it only reflects the quota. */
export function sportAccessMatrix(
  roadmap: readonly RoadmapSport[],
  expansionQuota: number,
): SportAccessMatrix {
  const expansion = roadmap.filter((s) => s.sport_code !== "football");
  const state: SportAccessState =
    expansionQuota === -1 ? "included" : expansionQuota > 0 ? "choose" : "upgrade";
  const rows: SportAccessRow[] = expansion.map((s) => ({
    sport_code: s.sport_code,
    display_name: s.display_name,
    phase: s.phase,
    state,
  }));
  let upgradeHint: string | null = null;
  if (expansionQuota === 0) {
    upgradeHint =
      "Your plan is football-only. Pro adds 1 expansion sport, Elite 3, Full Stack/Black all — applied automatically as each goes live.";
  } else if (expansionQuota > 0) {
    upgradeHint = `Your plan lets you pick ${expansionQuota} expansion sport${
      expansionQuota === 1 ? "" : "s"
    } once they go live. Upgrade for more.`;
  }
  return { rows, upgradeHint };
}

// ── Coverage UI upgrade — pure board view-model ──────────────────
// The Kalshi-style board: one normalised card model over live /
// activating / roadmap, an HONEST readiness % (real corpus math,
// clamped — never a fabricated trajectory), plus pure filter/sort/
// search so the page stays declarative + testable.

/** web_corpus / corpus_min as a clamped 0..100 integer. Returns
 *  null when the inputs are absent/invalid (honest "—", never a
 *  guessed bar). 100 only when the real corpus genuinely meets min. */
export function readinessPct(
  webCorpus: number | null | undefined,
  corpusMin: number | null | undefined,
): number | null {
  if (
    !Number.isFinite(webCorpus as number) ||
    !Number.isFinite(corpusMin as number) ||
    (corpusMin as number) <= 0 ||
    (webCorpus as number) < 0
  ) {
    return null;
  }
  return Math.max(
    0,
    Math.min(100, Math.round(((webCorpus as number) / (corpusMin as number)) * 100)),
  );
}

export type BoardStatus = "live" | "coming" | "roadmap";
export type BoardTab = "all" | "live" | "coming" | "roadmap";
export type BoardSort = "readiness" | "phase" | "alpha";

export interface BoardItem {
  readonly key: string;
  readonly kind: "league" | "sport";
  readonly status: BoardStatus;
  readonly title: string;
  readonly phase: string;
  readonly oddsSupported: boolean | null;
  /** 0..100 for `coming` leagues with corpus data; else null. */
  readonly readiness: number | null;
  readonly webCorpus: number | null;
  readonly corpusMin: number | null;
}

/** Flatten the overview into one ordered card list. liveNow is the
 *  gate truth (copied verbatim); a dormant league can never be a
 *  `live` card. football is represented only by live + coming, so
 *  the roadmap cards are the non-football sports. */
export function toBoardItems(o: CoverageOverview): BoardItem[] {
  const live: BoardItem[] = o.live.map((l) => ({
    key: `live:${l.sport_code}:${l.league_code}`,
    kind: "league",
    status: "live",
    title: l.league_name,
    phase: "1",
    oddsSupported: l.odds_supported,
    readiness: 100,
    webCorpus: null,
    corpusMin: null,
  }));
  const coming: BoardItem[] = o.football_activating.map((c) => {
    const r = readinessPct(c.web_corpus, c.corpus_min);
    return {
      key: `coming:football:${c.league_code}`,
      kind: "league",
      status: "coming",
      title: c.league_name,
      phase: c.phase,
      oddsSupported: c.odds_supported,
      readiness: r,
      webCorpus: typeof c.web_corpus === "number" ? c.web_corpus : null,
      corpusMin: typeof c.corpus_min === "number" ? c.corpus_min : null,
    };
  });
  const roadmap: BoardItem[] = o.roadmap
    .filter((s) => s.sport_code !== "football")
    .map((s) => ({
      key: `sport:${s.sport_code}`,
      kind: "sport",
      status: "roadmap",
      title: s.display_name,
      phase: s.phase,
      oddsSupported: null,
      readiness: null,
      webCorpus: null,
      corpusMin: null,
    }));
  return [...live, ...coming, ...roadmap];
}

export function filterBoard(
  items: readonly BoardItem[],
  opts: { tab: BoardTab; query: string },
): BoardItem[] {
  const q = opts.query.trim().toLowerCase();
  return items.filter((it) => {
    if (opts.tab !== "all" && it.status !== opts.tab) return false;
    if (q && !it.title.toLowerCase().includes(q)) return false;
    return true;
  });
}

export function sortBoard(
  items: readonly BoardItem[],
  by: BoardSort,
): BoardItem[] {
  const copy = [...items];
  if (by === "alpha") {
    copy.sort((a, b) => a.title.localeCompare(b.title));
  } else if (by === "phase") {
    copy.sort(
      (a, b) => a.phase.localeCompare(b.phase) || a.title.localeCompare(b.title),
    );
  } else {
    // readiness: closest-to-live first; null readiness sinks last,
    // but live (100) always tops. Stable tiebreak by title.
    copy.sort((a, b) => {
      const ra = a.readiness ?? -1;
      const rb = b.readiness ?? -1;
      return rb - ra || a.title.localeCompare(b.title);
    });
  }
  return copy;
}

/** The featured set for the auto-advancing hero: the coming leagues
 *  closest to live (highest real readiness), then live. Never
 *  fabricated — empty when nothing has accrued. */
export function featuredItems(
  items: readonly BoardItem[],
  max = 6,
): BoardItem[] {
  const coming = items
    .filter((i) => i.status === "coming" && (i.readiness ?? 0) > 0)
    .sort((a, b) => (b.readiness ?? 0) - (a.readiness ?? 0));
  const live = items.filter((i) => i.status === "live");
  return [...coming, ...live].slice(0, max);
}
